import { logout } from '@/services/api';
import { history, useIntl, useModel } from '@umijs/max';
import { Modal } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIdleTimer } from 'react-idle-timer';

export type IdleFormProps = {
  open: boolean;
  handleOpen: (open: boolean) => void;
};

const LIVE_TRANSCRIPTION_ACTIVE_KEY = 'liveTranscriptionActive';
const LIVE_TRANSCRIPTION_EVENT = 'live-transcription-status-change';
const DISABLE_IDLE_WARNING_KEY = 'DISABLE_IDLE_WARNING';

const IdleForm: FC = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const intl = useIntl();
  const [idle, setIdle] = useState(false);
  const [liveTranscriptionActive, setLiveTranscriptionActive] = useState(
    localStorage.getItem(LIVE_TRANSCRIPTION_ACTIVE_KEY) === 'true',
  );

  const isLiveTranscriptionActive = useCallback(
    () => localStorage.getItem(LIVE_TRANSCRIPTION_ACTIVE_KEY) === 'true',
    [],
  );

  const isIdleWarningDisabled = useCallback(
    () => localStorage.getItem(DISABLE_IDLE_WARNING_KEY) === 'true',
    [],
  );

  useEffect(() => {
    const handleLiveTranscriptionStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ active?: boolean }>;
      const active = Boolean(customEvent.detail?.active);
      setLiveTranscriptionActive(active);
    };

    window.addEventListener(LIVE_TRANSCRIPTION_EVENT, handleLiveTranscriptionStatusChange);

    return () => {
      window.removeEventListener(LIVE_TRANSCRIPTION_EVENT, handleLiveTranscriptionStatusChange);
    };
  }, []);

  useEffect(() => {
    if (liveTranscriptionActive && idle) {
      setIdle(false);
    }
  }, [liveTranscriptionActive, idle]);

  const onLogout = useCallback(async () => {
    flushSync(() => setInitialState({ ...initialState, currentUser: undefined } as any));
    localStorage.removeItem('tokenRefresh');
    await logout();
    localStorage.removeItem('token');
    history.push('/');
  }, [setInitialState]);

  const {} = useIdleTimer({
    onIdle: () => {
      if (!isIdleWarningDisabled() && !isLiveTranscriptionActive()) {
        setIdle(true);
      }
    },
  });

  const {} = useIdleTimer({
    onIdle: () => {
      if (!isIdleWarningDisabled() && idle && !isLiveTranscriptionActive()) {
        onLogout();
      }
    },
    timeout: 1800000,
  });

  return (
    <Modal
      centered
      title={intl.formatMessage({ id: 'idle.title' })}
      open={idle && !liveTranscriptionActive && !isIdleWarningDisabled()}
      closable={false}
      okText={intl.formatMessage({ id: 'logout' })}
      cancelText={intl.formatMessage({ id: 'resume' })}
      okButtonProps={{ danger: true }}
      cancelButtonProps={{ type: 'primary' }}
      onOk={onLogout}
      onCancel={() => setIdle(false)}
    >
      {intl.formatMessage({ id: 'idle.content' })}
    </Modal>
  );
};

export default IdleForm;
